import React from 'react';
import noop from 'lodash/noop';
import styled, { css } from 'styled-components';
import {
  AccordionButton,
  AccordionItem,
  AccordionItemBody,
  AccordionItemBodyWithAnimation,
  AccordionItemHeading,
  AccordionTitle,
  Accordion as AccordionWrapper,
} from '@sparknz/spark-pattern-library';

import useQuery from 'shared/hooks/useQueryParams';
import { shouldShowAccordion } from '../../Accordion.utils';

interface AccordionBodyProps {
  isOpen: boolean;
}

const AccordionBody = styled.div<AccordionBodyProps>`
  ${({ isOpen }) => css`
    display: ${isOpen ? 'block' : 'none'};
  `}
`;

type ConditionalAccordionItemBodyProps = Pick<
  AccordionDisplayItemProps,
  'isInEditor' | 'isOpen'
>;

const ConditionalAccordionItemBody = ({
  children,
  isInEditor,
  isOpen,
}: React.PropsWithChildren<ConditionalAccordionItemBodyProps>) => {
  /* This is to make the ordering/editing of content 
     possible in AEM editor mode */

  return isInEditor ? (
    <AccordionItemBody>{children}</AccordionItemBody>
  ) : (
    <AccordionItemBodyWithAnimation isOpen={isOpen}>
      {children}
    </AccordionItemBodyWithAnimation>
  );
};

interface AccordionDisplayItemProps {
  onClick: (key: string) => void;
  isSingleExpansion?: boolean;
  id: string;
  isOpen: boolean;
  title: string;
  index: number;
  children: React.ReactElement[];
  queryParamToUse: string;
  categories: string[];
  isInEditor: boolean;
}

const AccordionDisplayItem: React.FC<AccordionDisplayItemProps> = ({
  onClick,
  isSingleExpansion = false,
  id,
  isOpen = false,
  title,
  index,
  children,
  categories,
  queryParamToUse,
  isInEditor,
}) => {
  const query = useQuery();

  const selectedValue = query.get(queryParamToUse);
  const showAccordion = shouldShowAccordion(categories, selectedValue);
  if (!showAccordion) {
    return null;
  }

  const handleKeyPress = (event: React.KeyboardEvent, itemId: string) => {
    if (event.key === 'Enter' || event.key === ' ') {
      onClick(itemId);
    }
  };

  return (
    <AccordionWrapper
      allowMultipleExpanded={!isSingleExpansion}
      onChange={noop}
    >
      <AccordionItem key={id} uuid={id} isOpen={isOpen}>
        <div
          data-cmp-index={index}
          data-cmp-expanded={isOpen}
          onClick={() => onClick(id)}
          onKeyDown={(event) => handleKeyPress(event, id)}
        >
          <AccordionItemHeading dynamicTargetValue={id}>
            <AccordionTitle>{title}</AccordionTitle>
            <AccordionButton />
          </AccordionItemHeading>
        </div>

        <AccordionBody role="region" isOpen={isOpen}>
          <ConditionalAccordionItemBody isInEditor={isInEditor} isOpen={isOpen}>
            {children[index]}
          </ConditionalAccordionItemBody>
        </AccordionBody>
      </AccordionItem>
    </AccordionWrapper>
  );
};

export default AccordionDisplayItem;
